// Judge sheet column layouts.
//
// As of migration 20260602100000_create_judge_sheet_templates the canonical
// source of truth is the `judge_sheet_templates` table. The day-sheet API
// endpoint inlines the resolved columns and min_rows on its response, and the
// `JudgeSheetPrintable` component is a dumb presenter of those props.
//
// The map below + `getTemplateForEventType` are kept as a CLIENT-SIDE FALLBACK
// for events that predate the migration (their `judge_sheet_template_id` is
// NULL and the API has no columns to send). Don't add new entries — create a
// new row in the DB via the Settings → System Configuration page instead.

export type JudgeSheetTemplate = 'barrel_racing' | 'rough_stock' | 'timed';

export interface JudgeSheetColumn {
  /** Header text shown in the table head row. */
  label: string;
  /** Optional Tailwind class for column width tuning. */
  widthClass?: string;
}

/** Fallback column sets — must stay byte-identical with the seeded rows. */
export const JUDGE_SHEET_COLUMNS: Record<JudgeSheetTemplate, JudgeSheetColumn[]> = {
  barrel_racing: [
    { label: 'TIME' },
    { label: 'HAND TIME' },
    { label: 'BARREL +5' },
    { label: 'BARREL +5' },
    { label: 'BARREL +5' },
    { label: 'TOTAL TIME' },
  ],
  timed: [
    { label: 'ANIMAL DRAW' },
    { label: 'TIME' },
    { label: 'BARRIER +10' },
    { label: 'TOTAL TIME' },
  ],
  rough_stock: [
    { label: 'STOCK DRAW' },
    { label: 'RIDE' },
    { label: 'STOCK' },
    { label: 'SUBTOTAL' },
    { label: 'TOTAL' },
  ],
};

export const JUDGE_SHEET_MIN_ROWS = 17;

/**
 * Client-side classifier — mirrors public.compute_judge_sheet_template.
 * Used only when the API didn't return inline template columns AND no slug
 * is present on the event row.
 */
export function getTemplateForEventType(eventTypeName: string | null | undefined): JudgeSheetTemplate {
  if (!eventTypeName) return 'timed';
  if (eventTypeName === 'Barrel Racing') return 'barrel_racing';
  if (/Riding/i.test(eventTypeName)) return 'rough_stock';
  return 'timed';
}

/**
 * Resolve the {columns, minRows} pair from whatever the API gave us, with
 * a fallback through the static map. Centralises the precedence rules so
 * every caller renders the same thing for the same event.
 *
 * Precedence:
 *   1. Inline `judge_sheet_columns` + `judge_sheet_min_rows` from the API
 *   2. `judge_sheet_template` slug (matches a built-in)
 *   3. Discipline-based classifier on `event_type_name`
 */
export function resolveJudgeSheetLayout(opts: {
  columns?: JudgeSheetColumn[] | null;
  minRows?: number | null;
  templateSlug?: string | null;
  eventTypeName?: string | null;
}): { columns: JudgeSheetColumn[]; minRows: number } {
  if (Array.isArray(opts.columns) && opts.columns.length > 0) {
    return {
      columns: opts.columns,
      minRows: opts.minRows ?? JUDGE_SHEET_MIN_ROWS,
    };
  }
  const slug = (opts.templateSlug as JudgeSheetTemplate | undefined)
    ?? getTemplateForEventType(opts.eventTypeName);
  return {
    columns: JUDGE_SHEET_COLUMNS[slug] ?? JUDGE_SHEET_COLUMNS.timed,
    minRows: opts.minRows ?? JUDGE_SHEET_MIN_ROWS,
  };
}
